import { useCallback, useEffect, useState } from "react";
import { api, apiError, eur, dateFmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { useI18n } from "@/i18n";
import { PageHeader, EmptyState, Th, Td } from "@/components/common";
import { ListFilter, useListFilter, csvMoney } from "@/components/common/ListFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Undo2, Plus } from "lucide-react";

// Interne Codes bleiben unverändert; nur die Anzeige wird lokalisiert.
const DISPO = ["wiedereinlagerung", "zweite_wahl", "defekt", "abschreibung"];

export default function Returns() {
  const { user } = useAuth();
  const { t } = useI18n();
  const canEdit = can(user, "returns:edit");
  const [rows, setRows] = useState(null);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [reason, setReason] = useState("");
  const [lines, setLines] = useState([]);

  const load = useCallback(() => {
    api.get("/returns").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/orders?status=versandt").then((r) => setOrders(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const pickOrder = (id) => {
    setOrderId(id);
    const o = orders.find((x) => x.id === id);
    const ls = [];
    (o?.lines || []).forEach((l) => {
      if (l.variant_id) ls.push({ _k: crypto.randomUUID(), variant_id: l.variant_id, name: l.name, quantity: l.quantity, unit_price: l.unit_price, disposition: "wiedereinlagerung", refund_amount: (l.unit_price * l.quantity) });
    });
    setLines(ls);
  };
  const upd = (i, k, v) => {
    const l = [...lines];
    l[i] = { ...l[i], [k]: v };
    if (k === "quantity") l[i].refund_amount = (Number(l[i].unit_price) || 0) * (Number(v) || 0);
    setLines(l);
  };

  const save = async () => {
    try {
      await api.post("/returns", { order_id: orderId, reason,
        lines: lines.map((l) => ({ variant_id: l.variant_id, quantity: Number(l.quantity) || 0, disposition: l.disposition, refund_amount: Number(l.refund_amount) || 0 })) });
      toast.success(t("ret.booked")); setOpen(false); setOrderId(""); setReason(""); setLines([]); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const filter = useListFilter(rows, { fields: ["order_number", "reason", "status"], dateField: "created_at", statusField: "status", storageKey: "returns" });
  const view = filter.filtered;

  return (
    <div>
      <PageHeader title={t("ret.title")} subtitle={t("ret.subtitle")}
        actions={canEdit && <Button data-testid="new-return-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1.5" /> {t("ret.new")}</Button>} />

      <ListFilter filter={filter} testId="returns" withDates withStatus placeholder={t("filter.searchReference")}
        statusLabel={(s) => t(`ret.status.${s}`, s)}
        exportName="retouren" exportColumns={[
          { label: t("common.date"), value: (r) => dateFmt(r.created_at) },
          { label: t("common.order"), value: (r) => r.order_number || "" },
          { label: t("common.positions"), value: (r) => r.lines.length },
          { label: t("ret.refund"), value: (r) => csvMoney(r.refund_total) },
          { label: t("common.status"), value: (r) => t(`ret.status.${r.status}`, r.status) },
        ]} />

      {rows === null ? <div className="text-muted-foreground">{t("common.loading")}</div> :
        view.length === 0 ? <EmptyState testId="returns-empty" icon={Undo2} title={t("ret.emptyTitle")} hint={t("ret.emptyHint")} /> : (
          <div className="overflow-x-auto rounded-md border border-border bg-card shadow-sm">
            <table className="w-full"><thead className="bg-muted/50"><tr><Th>{t("common.date")}</Th><Th>{t("common.order")}</Th><Th right>{t("common.positions")}</Th><Th right>{t("ret.refund")}</Th><Th>{t("common.status")}</Th></tr></thead>
              <tbody data-testid="returns-table">{view.map((r) => (
                <tr key={r.id} className="hover:bg-muted/40"><Td>{dateFmt(r.created_at)}</Td><Td mono>{r.order_number || "—"}</Td>
                  <Td right mono>{r.lines.length}</Td><Td right mono>{eur(r.refund_total)}</Td><Td>{t(`ret.status.${r.status}`, r.status)}</Td></tr>))}
              </tbody></table>
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t("ret.new")}</DialogTitle></DialogHeader>
          <div><Label>{t("ret.shippedOrder")}</Label>
            <Select value={orderId} onValueChange={pickOrder}>
              <SelectTrigger className="mt-1.5" data-testid="return-order"><SelectValue placeholder={t("ret.chooseOrder")} /></SelectTrigger>
              <SelectContent>{orders.map((o) => <SelectItem key={o.id} value={o.id}>{o.order_number} · {o.channel}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {lines.length > 0 && (
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={l._k || i} className="grid grid-cols-12 gap-2 items-center">
                  <span className="col-span-3 text-sm truncate">{l.name}</span>
                  <Input className="col-span-2 h-9" type="number" value={l.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} data-testid={`return-qty-${i}`} />
                  <div className="col-span-4">
                    <Select value={l.disposition} onValueChange={(v) => upd(i, "disposition", v)}>
                      <SelectTrigger className="h-9" data-testid={`return-dispo-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{DISPO.map((k) => <SelectItem key={k} value={k}>{t(`ret.dispo.${k}`)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Input className="col-span-3 h-9" type="number" placeholder={t("ret.refund")} value={l.refund_amount} onChange={(e) => upd(i, "refund_amount", e.target.value)} />
                </div>
              ))}
            </div>
          )}
          <div><Label>{t("common.reason")}</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5" /></div>
          <DialogFooter><Button data-testid="save-return-btn" onClick={save} disabled={!orderId || !lines.length}>{t("ret.book")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
